/**
 * createAudioEngine — the WebAudio implementation of `AudioEngine` (ARCHITECTURE §2 "Audio").
 *
 * Graph (built once, when the context is created inside the first user gesture):
 *
 *   SFX voice pool ──► sfxMuffle ─► sfxDuck ─┐
 *   UI voice pool ────────────────┴► bus[sfx] ───────────┐
 *   music tracks ──► musicDuck ─────► bus[music] ────────┤
 *   crowd ─────────► crowdMuffle ─► crowdDuck ─► bus[crowd] ──┤
 *   booth ─► PA (high-pass + presence) ─► annMuffle ─► bus[announcer] ┤
 *   bus[sfx], bus[announcer] ─► reverb sends ─► stadium convolver ───────┤
 *                                                                        ▼
 *                          master (volume × mute) ─► glue compressor ─► brick-wall limiter ─► destination
 *
 * - Contexts without DynamicsCompressorNode (react-native-audio-api 0.13 on iOS/Android: no compressor yet) get
 *   `master ─► make-up trim ─► WaveShaper soft clip ─► destination` instead (`masterChainFor`); a context with neither
 *   connects master straight to the destination. Any failure while building the graph leaves the engine without a
 *   context (silent, retried on no later gesture) instead of half-initialised.
 * - Autoplay: nothing is created before `unlock()` (call it from a gesture; the engine also listens for the first
 *   pointer/key/touch itself). Calls before that are remembered (music, crowd) or caption-only (announcer).
 *   `eagerContext` creates the (suspended) context at engine creation instead: constructing an AudioContext can
 *   block the main thread for ~200 ms, which then never lands on the first click; the gesture only resumes it.
 * - Visibility: the context is suspended while the tab is hidden and resumed when it comes back.
 * - Pause menu (`setPaused`): gameplay buses are muffled and lowered; UI sounds and music stay clear.
 * - Leaving the stadium (`hush`): announcer, crowd reactions, chant and gameplay voices fade out.
 * - SFX bank: rendered to plain sample arrays in idle, time-budgeted slices from creation on (no context needed,
 *   so the title screen's idle time pays for it), wrapped into AudioBuffers on first play.
 * - iOS: opts into the `playback` audio session so the ring/silent switch does not silence the game.
 * - Zero leaks: voices and loop segments disconnect on `ended`; `dispose()` tears everything down.
 */
import { AssetStore, type Decoder, type Fetcher, type FileDecoder } from './assets';
import { Booth } from './announcer';
import { Crowd, glide } from './crowd';
import {
  AUDIO_BUSES,
  DEFAULT_VOLUMES,
  type AnnounceOptions,
  type AnnouncedLine,
  type AudioAsset,
  type AudioBus,
  type AudioEngine,
  type AudioLevels,
  type AudioStats,
  type AudioVolumes,
  type CrowdReaction,
  type LoadOptions,
  type PlayOptions,
} from './index';
import { LoopTrack } from './loop';
import { resampleLinear } from './baked';
import { renderSfx, sfxDuration } from './render';
import { createStadiumIr } from './reverb';
import type { RenderWorkerLike, SfxRenderReply } from './render-worker';
import { SFX_IDS, sfxDef, type SfxDef } from './sfx-bank';
import { VoicePool } from './voices';

type Listenable = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;

/**
 * The AudioContext the engine needs: the WebAudio `AudioContext`, with `createDynamicsCompressor` optional (and
 * `createWaveShaper` used only when present) — what react-native-audio-api's context provides (cast it: its classes
 * are structurally WebAudio-compatible but not declared as the DOM types).
 */
export type AudioContextLike = Omit<AudioContext, 'createDynamicsCompressor' | 'createWaveShaper'> & {
  createDynamicsCompressor?: AudioContext['createDynamicsCompressor'];
  createWaveShaper?: AudioContext['createWaveShaper'];
  /**
   * A gain that keeps its input's channel layout (WebAudio's default `max` mode), for the SFX voice chain only
   * (source → layer gain → voice gain → StereoPanner). A backend whose `createGain` is forced `explicit` stereo
   * (react-native-audio-api: the fan-out workaround) must provide it: a mono voice upmixed to stereo before its
   * panner pans by the balance law (+3 dB at centre, +6 dB on one side at hard pan) instead of WebAudio's
   * equal-power mono law. Default: `createGain`.
   */
  createVoiceGain?: () => GainNode;
};

/** Is `t` something the engine can listen on (a real window/document, not RN's bare `window` global)? */
const listenable = <T>(t: T | undefined): (T & Listenable) | null =>
  t && typeof (t as unknown as Listenable).addEventListener === 'function' ? (t as T & Listenable) : null;

export interface AudioEngineOptions {
  /**
   * Creates the live AudioContext (called once, from `unlock`). Default: `new AudioContext({latencyHint})`. Native:
   * react-native-audio-api's `new AudioContext()` cast to `AudioContextLike` (no compressor: soft-clip master).
   */
  createContext?: () => AudioContextLike | null;
  /** Decoder used before the live context exists. Default: a 1-frame OfflineAudioContext at 44.1 kHz. */
  createDecoder?: () => Decoder | null;
  /** Byte loader. Default: `fetch(url).arrayBuffer()`. */
  fetch?: Fetcher;
  /**
   * Decode assets in place instead of fetch + `decodeAudioData` (react-native-audio-api: the native decoder reads
   * the bundled file itself, no compressed bytes in JS, no context needed). Gets the asset and its resolved url.
   */
  decodeFile?: FileDecoder | null;
  /** Decoded music tracks kept in memory (default 2; 1 on low-memory phones: ≈ 11 MB of PCM per track). */
  musicCache?: number;
  /**
   * Master bus dynamics. Default: the best the context has (`masterChainFor`: compressor + limiter on WebAudio,
   * soft clip without a DynamicsCompressorNode). A forced chain the context cannot build falls back the same way.
   */
  masterChain?: MasterChain;
  /**
   * Level meters (`levels()`): an analyser on every bus and on the output, for diagnostics and calibration. Off by
   * default (each analyser costs a little audio-thread time).
   */
  meters?: boolean;
  /**
   * Stadium convolver (wet send from sfx + announcer). Default true. Set false on react-native-audio-api ≤ 0.13
   * Android: branching a bus into dry + Convolver wet/dry-mix glitches and can sound like every hit plays twice
   * (software-mansion/react-native-audio-api#1000; fixed in their 1.0 nightlies, not in 0.13.x).
   */
  reverb?: boolean;
  /**
   * The context plays every buffer at its own rate, ignoring the buffer's `sampleRate` (react-native-audio-api
   * 0.13): bank sounds rendered/decoded at another rate are resampled to the context rate before they are wrapped.
   */
  contextRateBuffers?: boolean;
  /** Prefix for root-relative asset URLs (Vite `import.meta.env.BASE_URL`). */
  baseUrl?: string;
  /** Where to listen for the first gesture (auto-unlock). Default: `window`. `null` disables. */
  gestureTarget?: Listenable | null;
  /** Visibility source (suspend while hidden). Default: `document`. `null` disables. */
  visibility?: (Listenable & { readonly hidden: boolean }) | null;
  /** Randomness for pitch variation (cosmetic). Default Math.random. */
  random?: () => number;
  /** Timer hooks (tests inject manual clocks). */
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  /**
   * Schedules one pre-render slice; `fn` gets its time budget in ms. Default: `requestIdleCallback` (budget =
   * the idle deadline, 3..8 ms), else a 16 ms `setTimeout` with a 4 ms budget (also used when `setTimeout` is
   * injected).
   */
  scheduleIdle?: (fn: (budgetMs: number) => void) => void;
  /** Millisecond clock for slice budgets. Default `performance.now`. */
  clock?: () => number;
  /** WebKit audio session (`navigator.audioSession`, Safari 16.4+). `null` disables. */
  audioSession?: { type: string } | null;
  /** Simultaneous gameplay SFX voices (default 24) and UI voices (default 6). */
  maxVoices?: number;
  uiVoices?: number;
  /** Pre-render the whole SFX bank in idle slices from creation on (default true). */
  prerender?: boolean;
  /**
   * Builds a Worker that renders the bank off the main thread (`render-worker.ts`; the app owns the Worker so its
   * bundler can find the entry). Without one, or if it fails, the bank renders in idle slices on the main thread.
   */
  renderWorker?: () => RenderWorkerLike | null;
  /** Warnings (failed assets). Default console.warn. */
  warn?: (msg: string) => void;
  /**
   * Create the AudioContext before the first gesture (it starts suspended until a gesture resumes it) instead of
   * inside it (default false). The constructor is the slow part of unlocking (~150-200 ms of main thread on some
   * machines). `true` = at engine creation; `'idle'` = once the page has painted and the main thread is idle
   * (`scheduleContext`), so it lands neither on the first paint nor on the first click. A gesture before then
   * creates it as usual.
   */
  eagerContext?: boolean | 'idle';
  /**
   * Schedules the `eagerContext: 'idle'` creation. Default: the next animation frame, then (after that frame
   * painted) the first idle moment ≥ 300 ms later (`requestIdleCallback`, 2 s timeout), else a 500 ms timeout.
   */
  scheduleContext?: (fn: () => void) => void;
}

/** Internal mix trims per bus (balance of the stems at volume 1). */
const BUS_TRIM: Record<Exclude<AudioBus, 'master'>, number> = {
  // Calibrated on an offline-rendered match mix (Chromium, DEFAULT_VOLUMES; files normalised, see public/audio):
  // announcer ≈ −15 LUFS, menu music ≈ −21, tackle ≈ −17, UI ≈ −20, crowd murmur ≈ −29 → mid-play −26 → roar
  // −17, touchdown moment ≈ −13 with true peaks ≤ −1.3 dBFS (the limiter never works hard).
  // v3 mix pass (Callum announcer, files at −16.4 LUFS): the crowd sits ~2 dB higher so the stadium feels alive
  // under the play (live play ≈ −23 LUFS-M, between plays ≈ −26, a roar ≈ −15); every line still clears the ducked
  // bed by ≥ 10 dB, and big calls also duck the gameplay SFX so TOUCH… DOWN!!! rides on top of its own fanfare.
  music: 1.9,
  sfx: 0.85,
  crowd: 2.6,
  announcer: 1.1,
};
/** Pre-compressor master trim (headroom for the glue compressor's automatic make-up gain). */
const MASTER_TRIM = 0.6;
/**
 * Ducking while the announcer speaks. Gameplay SFX only make room for the BIG calls (priority ≥ `bigFrom`:
 * TOUCHDOWN!!, PICKED OFF!!, IT'S GOOD!!…), which land on top of their own stingers (fanfare, air horn); colour
 * lines never soften a hit.
 */
const DUCK = {
  music: 0.35,
  crowd: 0.55,
  /** Big calls (priority ≥ bigFrom): the crowd gives a little more room and the gameplay SFX duck too. */
  crowdBig: 0.45,
  sfx: 0.5,
  bigFrom: 3,
  attack: 0.04,
  release: 0.35,
} as const;
/** Announcer slider at/above which ducking is at full depth (below, the depth scales down; ~0 = no duck). */
const DUCK_FULL_AT = 0.5;
/** Sample rate the bank is pre-rendered at before a context exists (played back resampled if it differs). */
const PRE_RATE = 48000;
const SLICE_BUDGET_MS = 4;
/** Most of an idle period a pre-render slice may use (ms; the browser's idle deadline is ≤ 50 ms). */
const SLICE_MAX_MS = 12;
/** Initial guess of the render cost (ms per rendered layer-sample; ~15 ms for a 1 s, 3-layer sound at 1×). */
const RENDER_MS_PER_SAMPLE = 1.2e-4;
const PAUSE = { cutoff: 650, level: 0.45, tau: 0.08 } as const;
const PUMP_MS = 100;
const VOLUME_TAU = 0.03;
const OPEN_CUTOFF = 20000;
/**
 * Pre-render order: the menus' UI sounds (heard first), then the big moments (a touchdown, the specials, a hurdle,
 * a sack…: the loudest, costliest sounds, and the ones a stall would ruin), then everything else.
 */
const PRERENDER_FIRST = [
  'td_fanfare',
  'special_telegraph',
  'special_hail_mary',
  'special_bulldozer',
  'special_mirage',
  'special_blitz',
  'hurdle_clear',
  'bulldoze',
  'truck_break',
  'sack',
  'tackle',
  'counter_tackle',
  'hitstop',
  'interception',
  'throw_hail_mary',
  'fake_cancel',
  'stiff_arm_hit',
  'fg_good',
  'fg_miss',
  'safety',
  'turnover',
  'final_horn',
  'overtime',
  'conversion_good',
  'conversion_fail',
  'first_down',
  'snap',
  'blitz_reveal',
  'whistle',
  'whistle_long',
];
const PRERENDER_ORDER: readonly string[] = [
  ...SFX_IDS.filter((id) => id.startsWith('ui_')),
  ...PRERENDER_FIRST.filter((id) => (SFX_IDS as readonly string[]).includes(id)),
  ...SFX_IDS.filter((id) => !id.startsWith('ui_') && !PRERENDER_FIRST.includes(id)),
];
/** A caption may show this much (s) before its line's first word; a line scheduled further ahead waits for it. */
const CAPTION_EARLY_S = 0.03;

/** Perceptual slider → gain: x^1.5 (half the slider ≈ −9 dB; gentler than x² at the top of the range). */
export const volumeToGain = (v: number): number => {
  const x = Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
  return x * Math.sqrt(x);
};

const clamp = (v: number | undefined, lo: number, hi: number, d: number): number =>
  v === undefined || !Number.isFinite(v) ? d : Math.min(hi, Math.max(lo, v));

interface Graph {
  master: GainNode;
  /** Glue compressor, or null (no DynamicsCompressorNode: see `masterChainFor`). */
  comp: DynamicsCompressorNode | null;
  /** Brick-wall limiter (a compressor), the soft-clip WaveShaper fallback, or null (master straight out). */
  limiter: AudioNode | null;
  /** Which master chain this context got. */
  masterChain: MasterChain;
  bus: Record<Exclude<AudioBus, 'master'>, GainNode>;
  sfxIn: GainNode;
  uiIn: GainNode;
  sfxMuffle: BiquadFilterNode;
  sfxDuck: GainNode;
  musicIn: GainNode;
  /** App-set music level (`setMusicLevel`: the results replay theater lowers the outro under its tapes). */
  musicLevel: GainNode;
  musicDuck: GainNode;
  crowdMuffle: BiquadFilterNode;
  crowdPause: GainNode;
  crowdDuck: GainNode;
  annIn: GainNode;
  annHp: BiquadFilterNode;
  annPresence: BiquadFilterNode;
  annMuffle: BiquadFilterNode;
  reverb: ConvolverNode | null;
  sends: GainNode[];
  /** Level meters (`meters` option): per bus, and `out` = what reaches the destination. */
  meters: Record<'out' | Exclude<AudioBus, 'master'>, AnalyserNode> | null;
  all: AudioNode[];
}

/** The master bus's dynamics: WebAudio compressor + limiter, the soft-clip fallback, or nothing. */
export type MasterChain = 'compressor' | 'softclip' | 'direct';

/** What master chain a context supports (react-native-audio-api 0.13: 'softclip'). */
export const masterChainFor = (c: AudioContextLike): MasterChain =>
  typeof c.createDynamicsCompressor === 'function'
    ? 'compressor'
    : typeof c.createWaveShaper === 'function'
      ? 'softclip'
      : 'direct';

/**
 * Soft-clip fallback (no compressor): the make-up gain the glue compressor would have added (MASTER_TRIM leaves
 * headroom for it) and a tanh curve that is linear to ≈ −6 dBFS and saturates smoothly at ±0.95, so a stacked
 * touchdown moment rounds off instead of clipping hard.
 */
const SOFTCLIP_MAKEUP = 1.45;
const SOFTCLIP_CEIL = 0.95;
const SOFTCLIP_KNEE = 0.5;
let softClipCurve: Float32Array<ArrayBuffer> | null = null;
export const softClipCurveSamples = (): Float32Array<ArrayBuffer> => {
  if (softClipCurve) return softClipCurve;
  const n = 2049;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const a = Math.abs(x);
    const y =
      a <= SOFTCLIP_KNEE
        ? a
        : SOFTCLIP_KNEE +
          (SOFTCLIP_CEIL - SOFTCLIP_KNEE) * Math.tanh((a - SOFTCLIP_KNEE) / (SOFTCLIP_CEIL - SOFTCLIP_KNEE));
    curve[i] = Math.sign(x) * y;
  }
  softClipCurve = curve;
  return curve;
};

/** The chain to build: `want` if the context can, else the best it can (compressor → soft clip → direct). */
const resolveMasterChain = (c: AudioContextLike, want: MasterChain | undefined): MasterChain => {
  const best = masterChainFor(c);
  if (!want || want === best) return best;
  if (want === 'direct') return 'direct';
  if (want === 'softclip') return typeof c.createWaveShaper === 'function' ? 'softclip' : 'direct';
  return best;
};

const METER_FFT = 1024;
const toDb = (x: number): number => (x > 1e-6 ? Math.round(200 * Math.log10(x)) / 10 : -120);

const defaultContext = (): AudioContextLike | null => {
  const g = globalThis as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const Ctor = g.AudioContext ?? g.webkitAudioContext;
  if (!Ctor) return null;
  try {
    return new Ctor({ latencyHint: 'interactive' });
  } catch {
    try {
      return new Ctor();
    } catch {
      return null;
    }
  }
};

const defaultDecoder = (): Decoder | null => {
  const g = globalThis as unknown as {
    OfflineAudioContext?: typeof OfflineAudioContext;
    webkitOfflineAudioContext?: typeof OfflineAudioContext;
  };
  const Ctor = g.OfflineAudioContext ?? g.webkitOfflineAudioContext;
  if (!Ctor) return null;
  try {
    return new Ctor(2, 1, 44100);
  } catch {
    return null;
  }
};

type IdleDeadlineLike = { timeRemaining(): number };
type IdleScheduler = (cb: (d: IdleDeadlineLike) => void, o?: { timeout: number }) => unknown;

/** After the next frame has painted, at the first idle moment ≥ 300 ms later (see `scheduleContext`). */
const defaultScheduleContext =
  (setT: (fn: () => void, ms: number) => unknown) =>
  (fn: () => void): void => {
    const g = globalThis as unknown as {
      requestAnimationFrame?: (cb: () => void) => unknown;
      requestIdleCallback?: IdleScheduler;
    };
    const idle = (): void => {
      if (typeof g.requestIdleCallback === 'function') g.requestIdleCallback(() => fn(), { timeout: 2000 });
      else setT(fn, 200);
    };
    if (typeof g.requestAnimationFrame === 'function') g.requestAnimationFrame(() => void setT(idle, 300));
    else setT(idle, 300);
  };

const defaultClock = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

const defaultAudioSession = (): { type: string } | null => {
  const nav = (globalThis as unknown as { navigator?: { audioSession?: { type: string } } }).navigator;
  return nav?.audioSession ?? null;
};

const defaultFetch: Fetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.arrayBuffer();
};

export const createAudioEngine = (options: AudioEngineOptions = {}): AudioEngine => {
  const g = globalThis as unknown as { window?: Listenable; document?: Listenable & { hidden: boolean } };
  const opt = {
    createContext: options.createContext ?? defaultContext,
    createDecoder: options.createDecoder ?? defaultDecoder,
    fetch: options.fetch ?? defaultFetch,
    baseUrl: options.baseUrl ?? '',
    // React Native has a `window` global without events and no `document`: only real event targets are defaults
    gestureTarget: options.gestureTarget === undefined ? listenable(g.window) : options.gestureTarget,
    visibility: options.visibility === undefined ? listenable(g.document) : options.visibility,
    random: options.random ?? Math.random,
    setInterval: options.setInterval ?? ((fn: () => void, ms: number) => setInterval(fn, ms)),
    clearInterval:
      options.clearInterval ?? ((h: unknown) => clearInterval(h as ReturnType<typeof setInterval>)),
    setTimeout: options.setTimeout ?? ((fn: () => void, ms: number) => setTimeout(fn, ms)),
    clock: options.clock ?? defaultClock,
    audioSession: options.audioSession === undefined ? defaultAudioSession() : options.audioSession,
    maxVoices: Math.max(4, options.maxVoices ?? 24),
    uiVoices: Math.max(1, options.uiVoices ?? 6),
    prerender: options.prerender ?? true,
    warn: options.warn ?? ((m: string) => console.warn(m)),
    eagerContext: options.eagerContext ?? false,
    meters: !!options.meters,
    reverb: options.reverb !== false,
  };
  const scheduleIdle: (fn: (budgetMs: number) => void) => void = (() => {
    if (options.scheduleIdle) return options.scheduleIdle;
    const ric = (globalThis as unknown as { requestIdleCallback?: IdleScheduler }).requestIdleCallback;
    // no timeout: a slice only ever runs in idle time (a forced slice used to land mid-frame during a match intro)
    if (!options.setTimeout && typeof ric === 'function')
      return (fn) => void ric((d) => fn(Math.max(0, Math.min(SLICE_MAX_MS, d.timeRemaining() - 1))));
    return (fn) => void opt.setTimeout(() => fn(SLICE_BUDGET_MS), 16);
  })();

  let ctx: AudioContext | null = null;
  let graph: Graph | null = null;
  let pool: VoicePool | null = null;
  let uiPool: VoicePool | null = null;
  let crowd: Crowd | null = null;
  let booth: Booth | null = null;
  let offlineDecoder: Decoder | null | undefined;
  let pump: unknown = null;
  let disposed = false;
  let muted = false;
  let paused = false;
  /** The pause muffle leaves the booth clear (instant replays: the announcer calls the tape). */
  let pausedBoothClear = false;
  let userSuspended = false;
  let resuming = false;
  let everRan = false;
  let volumes: AudioVolumes = { ...DEFAULT_VOLUMES };
  let crowdTarget = 0;
  let chantOn = false;
  let desiredMusic: string | null = null;
  let musicLevelTarget = 1;
  let musicFade = 1.2;
  let musicTrack: LoopTrack | null = null;
  const fadingTracks: LoopTrack[] = [];
  const synth = new Map<string, AudioBuffer | null>();
  /** Bank sounds rendered (before or after unlock) but not yet wrapped into an AudioBuffer. */
  const rendered = new Map<string, { data: Float32Array; rate: number }>();
  let sessionSet = false;
  const lastStart = new Map<string, number>();
  /** Bytes of PCM in the SFX bank (rendered samples + wrapped buffers). */
  const synthBytes = (): number => {
    let n = 0;
    for (const b of synth.values()) if (b) n += b.length * b.numberOfChannels * 4;
    for (const r of rendered.values()) n += r.data.byteLength;
    return n;
  };
  const unlockCbs = new Set<() => void>();
  const announceCbs = new Set<(l: AnnouncedLine) => void>();
  const lineEndCbs = new Set<(id: string, cut: boolean) => void>();
  const disposers: (() => void)[] = [];

  const assets = new AssetStore(
    opt.fetch,
    () => {
      if (ctx) return ctx;
      if (offlineDecoder === undefined) offlineDecoder = opt.createDecoder();
      return offlineDecoder;
    },
    opt.baseUrl,
    opt.warn,
    { decodeFile: options.decodeFile ?? null, musicCache: options.musicCache, setTimeout: opt.setTimeout },
  );

  const hidden = (): boolean => !!opt.visibility?.hidden;
  const canPlay = (): boolean =>
    !!ctx && !disposed && (ctx.state === 'running' || (resuming && !userSuspended && !hidden()));
  const trim = (id: string): number => {
    const t = assets.asset(id)?.gain;
    return t !== undefined && Number.isFinite(t) && t >= 0 ? t : 1;
  };

  // ── graph ─────────────────────────────────────────────────────────────────────────────────────────────
  const buildGraph = (c: AudioContext): Graph => {
    const masterChain = resolveMasterChain(c as AudioContextLike, options.masterChain);
    const all: AudioNode[] = [];
    const gain = (v = 1): GainNode => {
      const n = c.createGain();
      n.gain.value = v;
      all.push(n);
      return n;
    };
    const biquad = (type: BiquadFilterType, freq: number, q = 0.7, dB = 0): BiquadFilterNode => {
      const n = c.createBiquadFilter();
      n.type = type;
      n.frequency.value = freq;
      n.Q.value = q;
      n.gain.value = dB;
      all.push(n);
      return n;
    };
    const master = gain(masterGain());
    let comp: DynamicsCompressorNode | null = null;
    let limiter: AudioNode | null = null;
    if (masterChain === 'compressor') {
      const glue = c.createDynamicsCompressor();
      // gentle glue (note: Chrome adds automatic make-up gain to DynamicsCompressor; MASTER_TRIM compensates)
      glue.threshold.value = -12;
      glue.knee.value = 10;
      glue.ratio.value = 2.5;
      glue.attack.value = 0.004;
      glue.release.value = 0.2;
      const lim = c.createDynamicsCompressor();
      lim.threshold.value = -1.5;
      lim.knee.value = 0;
      lim.ratio.value = 20;
      lim.attack.value = 0.001;
      lim.release.value = 0.08;
      all.push(glue, lim);
      master.connect(glue);
      glue.connect(lim);
      lim.connect(c.destination);
      comp = glue;
      limiter = lim;
    } else if (masterChain === 'softclip') {
      const makeup = gain(SOFTCLIP_MAKEUP);
      const clip = c.createWaveShaper();
      clip.curve = softClipCurveSamples();
      all.push(clip);
      master.connect(makeup);
      makeup.connect(clip);
      clip.connect(c.destination);
      limiter = clip;
    } else master.connect(c.destination);

    const bus = {
      music: gain(busGain('music')),
      sfx: gain(busGain('sfx')),
      crowd: gain(busGain('crowd')),
      announcer: gain(busGain('announcer')),
    };
    for (const b of Object.values(bus)) b.connect(master);

    const sfxIn = gain();
    const uiIn = gain();
    const sfxMuffle = biquad('lowpass', OPEN_CUTOFF, 0.5);
    const sfxDuck = gain();
    sfxIn.connect(sfxMuffle);
    sfxMuffle.connect(sfxDuck);
    sfxDuck.connect(bus.sfx);
    uiIn.connect(bus.sfx);

    const musicIn = gain();
    const musicLevel = gain(musicLevelTarget);
    const musicDuck = gain();
    musicIn.connect(musicLevel);
    musicLevel.connect(musicDuck);
    musicDuck.connect(bus.music);

    const crowdMuffle = biquad('lowpass', OPEN_CUTOFF, 0.5);
    const crowdPause = gain();
    const crowdDuck = gain();
    crowdMuffle.connect(crowdPause);
    crowdPause.connect(crowdDuck);
    crowdDuck.connect(bus.crowd);

    // stadium PA: cut boom, lift presence a touch, then the (pause) muffle
    const annIn = gain();
    const annHp = biquad('highpass', 110, 0.7);
    const annPresence = biquad('peaking', 2800, 0.9, 3);
    const annMuffle = biquad('lowpass', OPEN_CUTOFF, 0.5);
    annIn.connect(annHp);
    annHp.connect(annPresence);
    annPresence.connect(annMuffle);
    annMuffle.connect(bus.announcer);

    let reverb: ConvolverNode | null = null;
    const sends: GainNode[] = [];
    // opt.reverb false: dry-only (no bus fan-out into Convolver). Needed on Android + audio-api 0.13 (#1000).
    if (opt.reverb)
      try {
        reverb = c.createConvolver();
        reverb.buffer = createStadiumIr(c);
        all.push(reverb);
        const ret = gain(0.9);
        reverb.connect(ret);
        ret.connect(master);
        const sfxSend = gain(0.11);
        const annSend = gain(0.2);
        bus.sfx.connect(sfxSend);
        bus.announcer.connect(annSend);
        sfxSend.connect(reverb);
        annSend.connect(reverb);
        sends.push(sfxSend, annSend, ret);
      } catch {
        reverb = null; // no convolver (very old engines): dry mix only
      }
    let meters: Graph['meters'] = null;
    if (opt.meters && typeof c.createAnalyser === 'function') {
      // bus taps are pulled through a silent sink so every engine processes them (some only render what reaches
      // the destination); the output meter sits INLINE before the destination (an analyser passes audio through),
      // so it reads exactly what is heard even on engines where a second consumer of a node gets no data
      const sink = gain(0);
      sink.connect(c.destination);
      const analyser = (): AnalyserNode => {
        const a = c.createAnalyser();
        a.fftSize = METER_FFT;
        all.push(a);
        return a;
      };
      const tap = (from: AudioNode): AnalyserNode => {
        const a = analyser();
        from.connect(a);
        a.connect(sink);
        return a;
      };
      const last = limiter ?? master;
      const out = analyser();
      last.disconnect();
      last.connect(out);
      out.connect(c.destination);
      meters = {
        out,
        music: tap(bus.music),
        sfx: tap(bus.sfx),
        crowd: tap(bus.crowd),
        announcer: tap(bus.announcer),
      };
    }
    return {
      master,
      comp,
      limiter,
      masterChain,
      bus,
      sfxIn,
      uiIn,
      sfxMuffle,
      sfxDuck,
      musicIn,
      musicLevel,
      musicDuck,
      crowdMuffle,
      crowdPause,
      crowdDuck,
      annIn,
      annHp,
      annPresence,
      annMuffle,
      reverb,
      sends,
      meters,
      all,
    };
  };

  let meterScratch: Float32Array<ArrayBuffer> | null = null;
  const readLevels = (): AudioLevels | null => {
    const m = graph?.meters;
    if (!m) return null;
    meterScratch ??= new Float32Array(METER_FFT);
    const buf = meterScratch;
    const one = (a: AnalyserNode): { rms: number; peak: number } => {
      a.getFloatTimeDomainData(buf);
      let sum = 0;
      let peak = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i]!;
        sum += v * v;
        const av = v < 0 ? -v : v;
        if (av > peak) peak = av;
      }
      return { rms: toDb(Math.sqrt(sum / buf.length)), peak: toDb(peak) };
    };
    return {
      out: one(m.out),
      music: one(m.music),
      sfx: one(m.sfx),
      crowd: one(m.crowd),
      announcer: one(m.announcer),
    };
  };

  function masterGain(): number {
    return muted ? 0 : volumeToGain(volumes.master) * MASTER_TRIM;
  }
  function busGain(b: Exclude<AudioBus, 'master'>): number {
    return volumeToGain(volumes[b]) * BUS_TRIM[b];
  }

  const applyVolumes = (): void => {
    if (!ctx || !graph) return;
    const now = ctx.currentTime;
    glide(graph.master.gain, masterGain(), now, VOLUME_TAU);
    for (const b of ['music', 'sfx', 'crowd', 'announcer'] as const)
      glide(graph.bus[b].gain, busGain(b), now, VOLUME_TAU);
  };

  const applyPaused = (): void => {
    if (!ctx || !graph) return;
    const now = ctx.currentTime;
    const cutoff = paused ? PAUSE.cutoff : OPEN_CUTOFF;
    glide(graph.sfxMuffle.frequency, cutoff, now, PAUSE.tau);
    glide(graph.crowdMuffle.frequency, cutoff, now, PAUSE.tau);
    glide(
      graph.annMuffle.frequency,
      paused && !pausedBoothClear ? PAUSE.cutoff : OPEN_CUTOFF,
      now,
      PAUSE.tau,
    );
    glide(graph.crowdPause.gain, paused ? PAUSE.level : 1, now, PAUSE.tau);
  };

  /** Duck depth 0..1: nothing to make room for when the announcer (or everything) is muted. */
  const duckDepth = (): number => {
    if (muted || volumes.master <= 0) return 0;
    return Math.min(1, Math.max(0, volumes.announcer / DUCK_FULL_AT));
  };

  const duckUntil = (until: number, priority = 1): void => {
    if (!ctx || !graph) return;
    const k = duckDepth();
    if (k < 0.02) return;
    const now = ctx.currentTime;
    const big = priority >= DUCK.bigFrom;
    const targets: [AudioParam, number][] = [
      [graph.musicDuck.gain, 1 - (1 - DUCK.music) * k],
      [graph.crowdDuck.gain, 1 - (1 - (big ? DUCK.crowdBig : DUCK.crowd)) * k],
    ];
    if (big) targets.push([graph.sfxDuck.gain, 1 - (1 - DUCK.sfx) * k]);
    for (const [p, level] of targets) {
      p.cancelScheduledValues(now);
      p.setValueAtTime(p.value, now);
      p.setTargetAtTime(level, now, DUCK.attack);
      p.setTargetAtTime(1, Math.max(now + 0.05, until), DUCK.release);
    }
  };

  const releaseDuck = (): void => {
    if (!ctx || !graph) return;
    const now = ctx.currentTime;
    for (const p of [graph.musicDuck.gain, graph.crowdDuck.gain, graph.sfxDuck.gain]) {
      p.cancelScheduledValues(now);
      p.setValueAtTime(p.value, now);
      p.setTargetAtTime(1, now, DUCK.release);
    }
  };

  // ── music ─────────────────────────────────────────────────────────────────────────────────────────────
  const applyMusic = (): void => {
    if (!ctx || !graph || disposed) return;
    const now = ctx.currentTime;
    if (musicTrack && musicTrack.id === desiredMusic && musicTrack.playing) return;
    if (musicTrack) {
      musicTrack.stop(now, musicFade);
      fadingTracks.push(musicTrack);
      musicTrack = null;
    }
    const id = desiredMusic;
    if (id === null) return;
    const buf = assets.buffer(id);
    if (!buf) {
      if (assets.has(id))
        void assets.decode(id).then((b) => {
          if (b && desiredMusic === id && !musicTrack) applyMusic();
        });
      return;
    }
    const a = assets.asset(id);
    const track = new LoopTrack(ctx, id, buf, graph.musicIn, {
      loop: a?.loop ?? true,
      xfade: a?.loopXfade,
      loopStart: a?.loopStart,
      loopEnd: a?.loopEnd,
    });
    track.start(now, musicFade, trim(id));
    musicTrack = track;
  };

  // ── sfx ───────────────────────────────────────────────────────────────────────────────────────────────
  /** Render a bank sound to samples (pure, no context needed): at the context rate once it exists. */
  const renderData = (id: string, def: SfxDef): void => {
    if (synth.has(id) || rendered.has(id)) return;
    const rate = ctx?.sampleRate ?? PRE_RATE;
    rendered.set(id, { data: renderSfx(def, rate), rate });
  };

  const synthBuffer = (id: string, def: SfxDef): AudioBuffer | null => {
    if (!ctx) return null;
    const cached = synth.get(id);
    if (cached !== undefined) return cached;
    renderData(id, def);
    let r = rendered.get(id)!;
    rendered.delete(id);
    if (options.contextRateBuffers && r.rate !== ctx.sampleRate)
      r = { data: resampleLinear(r.data, r.rate, ctx.sampleRate), rate: ctx.sampleRate };
    let buf: AudioBuffer | null = null;
    if (r.data.length > 0) {
      try {
        buf = ctx.createBuffer(1, r.data.length, r.rate);
      } catch {
        // a rate this engine refuses (very old WebKit): render again at the context's own rate
        const data = renderSfx(def, ctx.sampleRate);
        buf = data.length > 0 ? ctx.createBuffer(1, data.length, ctx.sampleRate) : null;
        buf?.getChannelData(0).set(data);
        synth.set(id, buf);
        return buf;
      }
      buf.getChannelData(0).set(r.data);
    }
    synth.set(id, buf);
    return buf;
  };

  /** A match or a lesson is ticking (`setBusy`): background work only takes what idle time the frames leave. */
  let busy = false;
  /** Learned render cost (ms per layer-sample), biased up so a slice under-books rather than overruns. */
  let msPerSample = RENDER_MS_PER_SAMPLE;
  const renderWork = (def: SfxDef): number => {
    let samples = 0;
    for (const l of def.layers) samples += sfxDuration({ ...def, layers: [{ ...l, at: 0 }] }) * PRE_RATE;
    return samples;
  };

  /**
   * Renders the bank in idle slices from creation on (no context needed: the title's idle time pays for it).
   * Before each sound the slice checks its estimated cost (learned from the sounds already rendered) against the
   * time it has left and stops rather than overrun the idle period. On the menus a slice always makes progress
   * (one sound even if it overruns: nothing is animating that matters); while a match or a lesson ticks
   * (`setBusy`) a sound that does not fit waits for a longer idle period — or renders on its first play.
   */
  const prerenderBank = (): void => {
    if (!opt.prerender) return;
    if (startRenderWorker()) return;
    prerenderSlices();
  };

  /**
   * Once a context exists, wrap the rendered samples into AudioBuffers in idle slices too (a few ms each at
   * worst), so a sound's first play in a match does not pay for the copy either.
   */
  let wrapScheduled = false;
  const scheduleWrap = (): void => {
    if (wrapScheduled || disposed || !ctx || rendered.size === 0) return;
    wrapScheduled = true;
    scheduleIdle((budgetMs) => {
      wrapScheduled = false;
      if (disposed || !ctx) return;
      const start = opt.clock();
      for (const id of [...rendered.keys()]) {
        const def = sfxDef(id);
        if (def) synthBuffer(id, def);
        if (opt.clock() - start >= budgetMs) break;
      }
      scheduleWrap();
    });
  };

  /** Main-thread fallback: the bank in idle slices (big-moment sounds first). */
  const prerenderSlices = (): void => {
    let i = 0;
    const slice = (budgetMs: number): void => {
      if (disposed) return;
      const start = opt.clock();
      let t = start;
      let done = 0;
      while (i < PRERENDER_ORDER.length) {
        const id = PRERENDER_ORDER[i]!;
        const def = sfxDef(id);
        if (!def || synth.has(id) || rendered.has(id)) {
          i++;
          continue;
        }
        const work = renderWork(def);
        if (t - start + work * msPerSample > budgetMs && (done > 0 || busy)) break;
        renderData(id, def);
        const now = opt.clock();
        const took = now - t;
        t = now;
        if (work > 0 && took > 0) {
          const rate = took / work;
          msPerSample = rate > msPerSample ? rate : msPerSample * 0.8 + rate * 0.2;
        }
        i++;
        done++;
      }
      if (i < PRERENDER_ORDER.length) scheduleIdle(slice);
      else scheduleWrap();
    };
    if (PRERENDER_ORDER.some((id) => !synth.has(id) && !rendered.has(id))) scheduleIdle(slice);
  };

  /**
   * Render the bank in a Worker (off the main thread: the title's animations and a match that starts a second
   * after boot never pay for it). Results land in `rendered` as they come; a sound played before its result
   * arrived renders on the spot as before. On any failure the rest renders in idle slices. False = no worker.
   */
  let worker: RenderWorkerLike | null = null;
  const stopWorker = (): void => {
    const w = worker;
    worker = null;
    if (!w) return;
    w.onmessage = null;
    w.onerror = null;
    try {
      w.terminate();
    } catch {
      /* already gone */
    }
  };
  const startRenderWorker = (): boolean => {
    if (!options.renderWorker) return false;
    let w: RenderWorkerLike | null = null;
    try {
      w = options.renderWorker();
    } catch {
      w = null;
    }
    if (!w) return false;
    worker = w;
    w.onmessage = (e: { data: SfxRenderReply }) => {
      if (disposed || worker !== w) return;
      const m = e.data;
      if ('done' in m) {
        stopWorker();
        scheduleWrap();
        return;
      }
      if (!m || typeof m.id !== 'string' || !(m.data instanceof Float32Array)) return;
      if (!synth.has(m.id) && !rendered.has(m.id)) rendered.set(m.id, { data: m.data, rate: m.rate });
    };
    w.onerror = () => {
      if (worker !== w) return;
      stopWorker();
      if (!disposed) prerenderSlices();
    };
    try {
      w.postMessage({ ids: PRERENDER_ORDER, rate: ctx?.sampleRate ?? PRE_RATE });
    } catch {
      stopWorker();
      return false;
    }
    return true;
  };

  const playSfx = (id: string, o: PlayOptions | undefined): void => {
    if (!canPlay() || !pool || !uiPool || !ctx) return;
    const now = ctx.currentTime;
    const def = sfxDef(id);
    const buffers: AudioBuffer[] = [];
    const gains: number[] = [];
    let priority = 1;
    let maxPerId = 3;
    let pitchVar = 0.03;
    let cooldown = 0.03;
    let ui = false;
    if (def) {
      cooldown = (def.cooldownMs ?? 30) / 1000;
      const last = lastStart.get(id);
      if (last !== undefined && now - last < cooldown) return;
      const file = def.file ? assets.buffer(def.file) : null;
      const fileGain = (def.fileGain ?? 1) * (def.file ? trim(def.file) : 1);
      if (file && def.fileMode === 'prefer') {
        buffers.push(file);
        gains.push(fileGain);
      } else {
        const s = synthBuffer(id, def);
        if (s) {
          buffers.push(s);
          gains.push(1);
        }
        if (file && def.fileMode === 'layer') {
          buffers.push(file);
          gains.push(fileGain);
        }
      }
      priority = def.priority ?? 1;
      maxPerId = def.maxVoices ?? 3;
      pitchVar = def.pitchVar ?? 0.03;
      ui = !!def.ui;
    } else {
      const a = assets.asset(id);
      const file = a && a.bus === 'sfx' ? assets.buffer(id) : null;
      if (!file) return;
      const last = lastStart.get(id);
      if (last !== undefined && now - last < cooldown) return;
      buffers.push(file);
      gains.push(trim(id));
    }
    if (buffers.length === 0) return;
    const rate = clamp((o?.pitch ?? 1) * (1 + (opt.random() * 2 - 1) * pitchVar), 0.25, 4, 1);
    const played = (ui ? uiPool : pool).play({
      id,
      priority,
      maxPerId,
      buffers,
      gains,
      volume: clamp(o?.volume, 0, 2, 1),
      rate,
      pan: clamp(o?.pan, -1, 1, 0),
    });
    if (played) lastStart.set(id, now);
  };

  // ── context lifecycle ─────────────────────────────────────────────────────────────────────────────────
  const fireUnlock = (): void => {
    if (everRan || !ctx || ctx.state !== 'running') return;
    everRan = true;
    for (const cb of [...unlockCbs]) {
      try {
        cb();
      } catch (e) {
        opt.warn(`[audio] onUnlock callback failed: ${String(e)}`);
      }
    }
  };

  const onPump = (): void => {
    if (!ctx || disposed) return;
    const now = ctx.currentTime;
    musicTrack?.pump(now);
    for (let i = fadingTracks.length - 1; i >= 0; i--) if (fadingTracks[i]!.done) fadingTracks.splice(i, 1);
    crowd?.pump(now);
    booth?.pump(now);
    if (!everRan && ctx.state === 'running') fireUnlock();
  };

  /** The context or its graph failed to build once: the engine stays silent (no retry on every gesture). */
  let contextFailed = false;

  const createContextOnce = (): boolean => {
    if (ctx) return true;
    if (disposed || contextFailed) return false;
    let c: AudioContext | null = null;
    try {
      c = opt.createContext() as AudioContext | null;
      if (!c) return false;
      return startContext(c);
    } catch (e) {
      // e.g. a context missing a node type the graph needs: never leave a half-built graph behind
      contextFailed = true;
      if (pump !== null) opt.clearInterval(pump);
      pump = null;
      ctx = null;
      graph = null;
      pool = null;
      uiPool = null;
      crowd = null;
      booth = null;
      try {
        void Promise.resolve(c?.close()).catch(() => undefined);
      } catch {
        /* ignore */
      }
      opt.warn(`[audio] could not start the audio graph: ${String(e)}`);
      return false;
    }
  };

  const startContext = (c: AudioContext): boolean => {
    ctx = c;
    graph = buildGraph(c);
    const like = c as unknown as AudioContextLike;
    const voiceGain = typeof like.createVoiceGain === 'function' ? like.createVoiceGain.bind(like) : undefined;
    pool = new VoicePool(c, graph.sfxIn, opt.maxVoices, voiceGain);
    uiPool = new VoicePool(c, graph.uiIn, opt.uiVoices, voiceGain);
    crowd = new Crowd(c, graph.crowdMuffle, {
      buffer: (id) => assets.buffer(id),
      trim,
      xfade: (id) => assets.asset(id)?.loopXfade,
      random: opt.random,
    });
    booth = new Booth(c, graph.annIn, {
      buffer: (id) => {
        const b = assets.buffer(id);
        // a line asked for before its file was fetched (deferred loading): get it ready for the next time
        if (!b && assets.has(id)) void assets.decode(id);
        return b;
      },
      tail: (id) => {
        const b = assets.buffer(id);
        return b ? trailingSilence(b) : 0;
      },
      breaks: (id) => {
        const b = assets.buffer(id);
        return b ? phraseBreaks(b) : (assets.asset(id)?.breaks ?? []);
      },
      caption: (id) => captionOf(id),
      duration: (id) => assets.asset(id)?.duration ?? 1.2,
      trim,
      duck: duckUntil,
      audible: canPlay,
      emit: (line, at) => {
        // a line scheduled ahead (a bigger call waiting for a phrase break, a follow-up): its caption shows
        // when its first word does, not when the booth decided
        const delay = at !== undefined && ctx ? at - ctx.currentTime : 0;
        if (delay <= CAPTION_EARLY_S) {
          emitLine(line);
          return;
        }
        clearPendingStart(line.id);
        const token = { cancelled: false };
        pendingStarts.set(line.id, token);
        opt.setTimeout(() => {
          if (token.cancelled) return;
          pendingStarts.delete(line.id);
          if (!disposed) emitLine(line);
        }, delay * 1000);
      },
      ended: (id, cut, at) => {
        // a line dropped before its first word: it was never shown, nothing to take down
        if (pendingStarts.has(id)) {
          clearPendingStart(id);
          return;
        }
        const delay = at !== undefined && ctx ? at - ctx.currentTime : 0;
        if (delay <= CAPTION_EARLY_S) lineEnded(id, cut);
        // cut at its next phrase break: the caption stays up while the phrase is still heard
        else opt.setTimeout(() => void (!disposed && lineEnded(id, cut)), delay * 1000);
      },
    });
    const onState = (): void => {
      if (c.state === 'running') {
        resuming = false;
        fireUnlock();
      }
    };
    c.addEventListener?.('statechange', onState);
    disposers.push(() => c.removeEventListener?.('statechange', onState));
    pump = opt.setInterval(onPump, PUMP_MS);
    applyPaused();
    crowd.setIntensity(crowdTarget);
    crowd.setChant(chantOn);
    void assets.decodeAllPending();
    if (!worker) scheduleWrap();
    return true;
  };

  /** iOS/Safari: a Web Audio page is an "ambient" session by default (muted by the silent switch). */
  const setPlaybackSession = (): void => {
    if (sessionSet) return;
    sessionSet = true;
    const session = opt.audioSession;
    if (!session) return;
    try {
      if (session.type !== 'playback') session.type = 'playback';
    } catch {
      /* read-only / unsupported value: keep the browser default */
    }
  };

  /** Resume the context unless the user/visibility wants it suspended. */
  const tryResume = (): Promise<void> => {
    if (!ctx || disposed || userSuspended || hidden() || ctx.state === 'running' || ctx.state === 'closed')
      return Promise.resolve();
    resuming = true;
    // iOS needs a sound started inside the gesture to fully unlock output.
    try {
      const b = ctx.createBuffer(1, 1, ctx.sampleRate);
      const s = ctx.createBufferSource();
      s.buffer = b;
      s.connect(ctx.destination);
      s.onended = () => s.disconnect();
      s.start(0);
    } catch {
      /* ignore */
    }
    let p: Promise<void>;
    try {
      p = Promise.resolve(ctx.resume());
    } catch (e) {
      p = Promise.reject(e);
    }
    return p.then(
      () => {
        resuming = false;
        fireUnlock();
        applyMusic();
      },
      () => {
        resuming = false;
      },
    );
  };

  const captionOf = (id: string): string | null => {
    const a = assets.asset(id);
    if (!a || a.bus !== 'announcer') return null;
    return a.caption ?? id.replace(/_/g, ' ').toUpperCase();
  };

  /** Caption starts scheduled for later (by line id). */
  const pendingStarts = new Map<string, { cancelled: boolean }>();
  const clearPendingStart = (id: string): void => {
    const t = pendingStarts.get(id);
    if (!t) return;
    t.cancelled = true;
    pendingStarts.delete(id);
  };
  const lineEnded = (id: string, cut: boolean): void => {
    for (const cb of [...lineEndCbs]) {
      try {
        cb(id, cut);
      } catch (e) {
        opt.warn(`[audio] onLineEnd callback failed: ${String(e)}`);
      }
    }
  };

  const emitLine = (line: AnnouncedLine): void => {
    for (const cb of [...announceCbs]) {
      try {
        cb(line);
      } catch (e) {
        opt.warn(`[audio] onAnnounce callback failed: ${String(e)}`);
      }
    }
  };

  // gesture auto-unlock (kept installed: iOS may interrupt the context later and needs a gesture again)
  if (opt.gestureTarget) {
    const target = opt.gestureTarget;
    const onGesture = (): void => {
      if (disposed) return;
      if (!ctx || (ctx.state !== 'running' && !userSuspended && !hidden())) void engine.unlock();
    };
    const evs = ['pointerdown', 'keydown', 'touchend', 'mousedown'] as const;
    for (const e of evs) target.addEventListener(e, onGesture, { capture: true, passive: true });
    disposers.push(() => {
      for (const e of evs) target.removeEventListener(e, onGesture, { capture: true });
    });
  }
  if (opt.visibility) {
    const vis = opt.visibility;
    const onVis = (): void => {
      if (!ctx || disposed) return;
      if (vis.hidden) {
        if (ctx.state === 'running') void ctx.suspend().catch(() => {});
      } else void tryResume();
    };
    vis.addEventListener('visibilitychange', onVis);
    disposers.push(() => vis.removeEventListener('visibilitychange', onVis));
  }

  const engine: AudioEngine = {
    setBusy(b: boolean): void {
      busy = !!b;
    },

    get unlocked(): boolean {
      return !!ctx && ctx.state === 'running' && !disposed;
    },

    unlock(): Promise<void> {
      if (disposed) return Promise.resolve();
      setPlaybackSession();
      if (!createContextOnce()) return Promise.resolve();
      const p = tryResume();
      // a context created inside a gesture is often running already (no resume round-trip)
      fireUnlock();
      applyMusic();
      return p;
    },

    load(list: readonly AudioAsset[], o?: LoadOptions): Promise<void> {
      if (disposed || !Array.isArray(list)) return Promise.resolve();
      const registered = assets.register(list, { defer: !!o?.defer });
      // a track requested before its asset was known: start decoding it with everything else
      if (desiredMusic && assets.has(desiredMusic)) void assets.decode(desiredMusic);
      return registered.then(() => {
        if (desiredMusic && !musicTrack) applyMusic();
      });
    },

    prefetch(filter?: (asset: AudioAsset) => boolean, o?: { urgent?: boolean }): Promise<void> {
      if (disposed) return Promise.resolve();
      return assets.prefetch(filter, { urgent: !!o?.urgent }).then(() => {
        if (desiredMusic && !musicTrack) applyMusic();
      });
    },

    cancelLine(id: string, o?: { fadeMs?: number; graceMs?: number }): boolean {
      if (disposed || !booth || typeof id !== 'string') return false;
      const fade = o?.fadeMs;
      const grace = o?.graceMs;
      return booth.cancel(
        id,
        fade !== undefined && Number.isFinite(fade) ? clamp(fade / 1000, 0.005, 2, 0.2) : undefined,
        grace !== undefined && Number.isFinite(grace) ? clamp(grace / 1000, 0, 10, 0) : undefined,
      );
    },

    setMusicLevel(level: number, fadeMs = 600): void {
      if (disposed) return;
      musicLevelTarget = clamp(level, 0, 1, 1);
      if (!ctx || !graph) return;
      const now = ctx.currentTime;
      glide(
        graph.musicLevel.gain,
        musicLevelTarget,
        now,
        Math.max(0.005, clamp(fadeMs, 0, 5000, 600) / 3000),
      );
    },

    setVolumes(v: Partial<AudioVolumes>): void {
      const next = { ...volumes };
      for (const b of AUDIO_BUSES) {
        const x = v[b];
        if (x !== undefined && Number.isFinite(x)) next[b] = Math.min(1, Math.max(0, x));
      }
      volumes = next;
      applyVolumes();
    },

    getVolumes: () => ({ ...volumes }),

    setMuted(m: boolean): void {
      muted = !!m;
      applyVolumes();
    },

    sfx(id: string, o?: PlayOptions): void {
      if (disposed || typeof id !== 'string') return;
      playSfx(id, o);
    },

    announce(id: string, o?: AnnounceOptions): string | null {
      if (disposed) return null;
      if (booth && canPlay() && assets.has(id) && !assets.buffer(id)) {
        // the file is not loaded yet (tiered loading): a silent caption would mislead — refuse (the bridge falls
        // through to another take) and get it ready for next time
        void assets.decode(id);
        return null;
      }
      if (booth) return booth.say(id, o);
      // no context yet: caption only
      const caption = captionOf(id);
      if (caption !== null) {
        const d = assets.asset(id)?.duration ?? 1.2;
        emitLine({ id, caption, durationMs: Math.round(Math.max(1.2, d + 0.9) * 1000) });
      }
      return caption;
    },

    music(trackId: string | null, fadeMs = 1200): void {
      if (disposed) return;
      musicFade = Math.max(0.01, (Number.isFinite(fadeMs) ? fadeMs : 1200) / 1000);
      if (trackId === desiredMusic && (trackId === null || musicTrack?.id === trackId)) return;
      desiredMusic = trackId;
      // decode now (offline decoder before unlock) so the track can start the moment audio is allowed
      if (trackId !== null && !ctx && assets.has(trackId)) void assets.decode(trackId);
      applyMusic();
    },

    crowd(intensity: number): void {
      if (disposed) return;
      crowdTarget = clamp(intensity, 0, 1, 0);
      crowd?.setIntensity(crowdTarget);
    },

    crowdReact(kind: CrowdReaction, strength = 1): void {
      if (disposed || !canPlay()) return;
      crowd?.react(kind, strength);
    },

    suspend(): void {
      userSuspended = true;
      if (ctx && ctx.state === 'running') void ctx.suspend().catch(() => {});
    },

    resume(): void {
      userSuspended = false;
      void tryResume();
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      if (pump !== null) opt.clearInterval(pump);
      pump = null;
      for (const d of disposers.splice(0)) d();
      for (const id of [...pendingStarts.keys()]) clearPendingStart(id);
      stopWorker();
      musicTrack?.dispose();
      musicTrack = null;
      for (const t of fadingTracks.splice(0)) t.dispose();
      pool?.dispose();
      uiPool?.dispose();
      crowd?.dispose();
      booth?.dispose();
      if (graph) for (const n of graph.all) n.disconnect();
      graph = null;
      pool = uiPool = null;
      crowd = null;
      booth = null;
      synth.clear();
      rendered.clear();
      lastStart.clear();
      unlockCbs.clear();
      announceCbs.clear();
      lineEndCbs.clear();
      assets.dispose();
      const c = ctx;
      ctx = null;
      if (c && c.state !== 'closed') void c.close().catch(() => {});
    },

    onUnlock(cb: () => void): () => void {
      if (everRan) {
        cb();
        return () => {};
      }
      unlockCbs.add(cb);
      return () => void unlockCbs.delete(cb);
    },

    onAnnounce(cb: (l: AnnouncedLine) => void): () => void {
      announceCbs.add(cb);
      return () => void announceCbs.delete(cb);
    },

    setPaused(p: boolean, o?: { booth?: boolean }): void {
      const clear = !!p && o?.booth === false;
      if (paused === !!p && pausedBoothClear === clear) return;
      paused = !!p;
      pausedBoothClear = clear;
      applyPaused();
    },

    onLineEnd(cb: (id: string, cut: boolean) => void): () => void {
      lineEndCbs.add(cb);
      return () => void lineEndCbs.delete(cb);
    },

    crowdChant(on: boolean): void {
      chantOn = !!on;
      crowd?.setChant(chantOn);
    },

    hush(fadeMs = 300): void {
      if (disposed) return;
      const fade = clamp(fadeMs / 1000, 0.005, 3, 0.3);
      chantOn = false;
      crowd?.setChant(false);
      crowd?.hushReactions(fade);
      booth?.hush(fade);
      pool?.stopAll(fade);
      releaseDuck();
    },

    addRendered(sounds: ReadonlyMap<string, { data: Float32Array; rate: number }>): number {
      if (disposed) return 0;
      let n = 0;
      for (const [id, r] of sounds) {
        if (!sfxDef(id) || synth.has(id) || rendered.has(id) || !(r.data instanceof Float32Array)) continue;
        rendered.set(id, { data: r.data, rate: r.rate });
        n++;
      }
      if (n > 0) scheduleWrap();
      return n;
    },

    stats(): AudioStats {
      const state: AudioStats['state'] = disposed
        ? 'closed'
        : !ctx
          ? 'locked'
          : ctx.state === 'running'
            ? 'running'
            : ctx.state === 'closed'
              ? 'closed'
              : 'suspended';
      return {
        state,
        voices: (pool?.active ?? 0) + (uiPool?.active ?? 0),
        loaded: assets.loaded,
        registered: assets.registered,
        music: musicTrack?.id ?? null,
        crowd: crowdTarget,
        speaking: booth?.current ?? null,
        queued: booth?.queued ?? 0,
        master: graph?.masterChain ?? null,
        pcmBytes: assets.decodedBytes + synthBytes(),
      };
    },

    levels(): AudioLevels | null {
      if (disposed) return null;
      return readLevels();
    },
  };
  prerenderBank();
  if (opt.eagerContext === 'idle') {
    const schedule = options.scheduleContext ?? defaultScheduleContext(opt.setTimeout);
    schedule(() => {
      if (!disposed && !ctx) createContextOnce();
    });
  } else if (opt.eagerContext) createContextOnce();
  return engine;
};

/** Below this level (≈ −45 dBFS) a line's last samples count as padding. */
const SILENCE_LEVEL = 0.0056;
const tails = new WeakMap<AudioBuffer, number>();

/**
 * Trailing silence of a decoded line (s), cached per buffer. A buffer that is silent all the way through (mocks, a
 * broken file) has no tail: its length is all it has.
 */
export const trailingSilence = (b: AudioBuffer): number => {
  const cached = tails.get(b);
  if (cached !== undefined) return cached;
  let tail = 0;
  try {
    const n = b.length;
    const max = Math.min(n, Math.ceil(b.sampleRate * 0.6));
    let last = -1;
    for (let ch = 0; ch < b.numberOfChannels; ch++) {
      const d = b.getChannelData(ch);
      for (let i = n - 1; i >= n - max && i > last; i--) {
        const v = d[i]!;
        if (v > SILENCE_LEVEL || v < -SILENCE_LEVEL) {
          last = i;
          break;
        }
      }
    }
    // nothing audible in the last 0.6 s (a silent mock, a broken file): no tail to trim
    tail = last < 0 ? 0 : (n - 1 - last) / b.sampleRate;
  } catch {
    tail = 0;
  }
  tails.set(b, tail);
  return tail;
};

/** Phrase-break analysis: 20 ms windows; a pause is ≥ 100 ms below 4 % of the line's loudest window (the PA echo
 * keeps a little energy in real pauses), after at least 0.4 s of voice (so "PICKED… OFF" is one phrase). */
const BREAK_WINDOW_S = 0.02;
const BREAK_LEVEL = 0.04;
const BREAK_MIN_GAP = 5;
const BREAK_MIN_VOICE = 20;
const breakCache = new WeakMap<AudioBuffer, readonly number[]>();

/**
 * Phrase breaks of a decoded line (s from its start, ascending; cached per buffer): where a pause between two
 * phrases begins ("SPIN MOVE!! | Oh, that's nasty!" → [1.44]). The booth lets a line reach one before a bigger
 * call cuts it, so nothing is clipped mid-word. Silent or unreadable buffers have none.
 */
export const phraseBreaks = (b: AudioBuffer): readonly number[] => {
  const cached = breakCache.get(b);
  if (cached) return cached;
  const out: number[] = [];
  try {
    const win = Math.max(1, Math.round(b.sampleRate * BREAK_WINDOW_S));
    const n = Math.floor(b.length / win);
    const env = new Float32Array(n);
    for (let ch = 0; ch < b.numberOfChannels; ch++) {
      const d = b.getChannelData(ch);
      for (let w = 0; w < n; w++) {
        let sum = 0;
        for (let i = w * win, end = i + win; i < end; i++) sum += d[i]! * d[i]!;
        env[w] = Math.max(env[w]!, Math.sqrt(sum / win));
      }
    }
    let peak = 0;
    for (let w = 0; w < n; w++) peak = Math.max(peak, env[w]!);
    if (peak > SILENCE_LEVEL) {
      const thr = peak * BREAK_LEVEL;
      let last = n - 1;
      while (last >= 0 && env[last]! <= thr) last--;
      let run = 0;
      let voiced = 0;
      for (let w = 0; w <= last; w++) {
        if (env[w]! <= thr) run++;
        else {
          if (run >= BREAK_MIN_GAP && voiced >= BREAK_MIN_VOICE)
            out.push(Math.round(((w - run) * win * 1000) / b.sampleRate) / 1000);
          run = 0;
          voiced++;
        }
      }
    }
  } catch {
    out.length = 0;
  }
  breakCache.set(b, out);
  return out;
};
